// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { FacebookPixelUtils } from "../../../../../../io/hyperify/core/facebook/FacebookPixelUtils";
import { Currency } from "../../../../../../io/hyperify/core/types/Currency";
import { FACEBOOK_PIXEL_ID } from "../../../../core/constants/ads";
import {
    CUSTOMER_SERVICE_CLASS_NAME,
    CUSTOMER_SERVICE_VIEW_CLASS_NAME
} from "../../../../core/constants/className";
import {
    T_CUSTOMER_SERVICE_CLOUD_CONSULTING_CONTENT,
    T_CUSTOMER_SERVICE_CLOUD_CONSULTING_TITLE,
    T_CUSTOMER_SERVICE_CONTENT,
    T_CUSTOMER_SERVICE_CUSTOM_SOLUTIONS_CONTENT,
    T_CUSTOMER_SERVICE_CUSTOM_SOLUTIONS_TITLE,
    T_CUSTOMER_SERVICE_FOOTER_CONTENT,
    T_CUSTOMER_SERVICE_GLOBAL_PARTNER_CONTENT,
    T_CUSTOMER_SERVICE_GLOBAL_PARTNER_TITLE,
    T_CUSTOMER_SERVICE_IT_CONSULTING_CONTENT,
    T_CUSTOMER_SERVICE_IT_CONSULTING_TITLE,
    T_CUSTOMER_SERVICE_SERVICES_CONTENT,
    T_CUSTOMER_SERVICE_SERVICES_TITLE,
    T_CUSTOMER_SERVICE_SOFTWARE_CONSULTING_CONTENT,
    T_CUSTOMER_SERVICE_SOFTWARE_CONSULTING_TITLE,
    T_CUSTOMER_SERVICE_TEXT_CONTENT,
    T_CUSTOMER_SERVICE_TITLE,
    T_SITE_NAME,
} from "../../../../core/constants/translation";
import { FacebookPixel } from "../../common/facebookPixel/FacebookPixel";
import { ProductArticle } from "../../common/productArticle/ProductArticle";
import {
    CUSTOMER_SERVICE_ROUTE,
} from "../../../../core/constants/route";
import { SUPPORT_IMAGE } from "../../../assets/images";
import {
    COMPANY_EMAIL,
    COMPANY_PHONE_NUMBER_DISPLAY,
    COMPANY_PHONE_NUMBER_FULL,
    MAINTENANCE_PHONE_NUMBER_DISPLAY,
    MAINTENANCE_PHONE_NUMBER_FULL,
} from "../../../../core/constants/company";
import {
    ComponentNodeMap,
    ComponentUtils,
} from "../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import React, { ReactNode } from "react";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import { SEODiv } from "../../common/seo/SEODiv";
import "./CustomerServiceView.scss";

export interface CustomerServiceViewProps {
    readonly t: TranslationFunction;
    readonly className?: string;
}

export function CustomerServiceView (props: CustomerServiceViewProps) {

    const t: TranslationFunction = props?.t ?? ((key: string): string => key);
    const className = props?.className;

    const HG_HREF = "https://www.heusalagroup.fi";
    const HG_LABEL = "Heusala Group Oy";

    const paramBlocks : ComponentNodeMap = {
        EMAIL: <a href={'mailto:'+COMPANY_EMAIL}>{COMPANY_EMAIL}</a>,
        PHONE: <a href={'tel:'+COMPANY_PHONE_NUMBER_FULL}>{COMPANY_PHONE_NUMBER_DISPLAY}</a>,
        SUPPORT_PHONE: <a href={'tel:'+MAINTENANCE_PHONE_NUMBER_FULL}>{MAINTENANCE_PHONE_NUMBER_DISPLAY}</a>,
        HG_LINK: <a href={HG_HREF}>{HG_LABEL}</a>,
    };

    const description: ReactNode[] = t(T_CUSTOMER_SERVICE_CONTENT).split(/\n+/);

    const translationParams = {
        EMAIL: COMPANY_EMAIL,
        PHONE: COMPANY_PHONE_NUMBER_DISPLAY,
        SUPPORT_PHONE: MAINTENANCE_PHONE_NUMBER_DISPLAY,
        HG_LINK: HG_LABEL,
    };

    return (
        <SEODiv className={CUSTOMER_SERVICE_CLASS_NAME}
                path={CUSTOMER_SERVICE_ROUTE}
                title={t(T_CUSTOMER_SERVICE_TITLE)}
                description={t(T_CUSTOMER_SERVICE_TEXT_CONTENT, translationParams)}
                siteName={t(T_SITE_NAME)}
        >
            <FacebookPixel url={FacebookPixelUtils.getViewContentURL(FACEBOOK_PIXEL_ID, 0, Currency.EUR )} />

            <article
                className={
                    CUSTOMER_SERVICE_VIEW_CLASS_NAME +
                    (className ? ` ${ className }` : "")
                }
            >
                <ProductArticle
                    t={ t }
                    className="view"
                    previewImage={ SUPPORT_IMAGE }
                    title={ t( T_CUSTOMER_SERVICE_TITLE ) }
                    description={ description }
                    paramBlocks={ paramBlocks }
                    // primaryButtonTo={ SENDANOR_BOOKING_URL }
                    // primaryButtonText={ t(T_CUSTOMER_SERVICE_BUTTON_TEXT) }
                />

                <div className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info' }>

                    <section className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info-item' }>
                        <h3>{ t( T_CUSTOMER_SERVICE_CUSTOM_SOLUTIONS_TITLE ) }</h3>
                        <p>{ t( T_CUSTOMER_SERVICE_CUSTOM_SOLUTIONS_CONTENT ) }</p>
                    </section>

                    <section className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info-item' }>
                        <h3>{ t( T_CUSTOMER_SERVICE_SERVICES_TITLE ) }</h3>
                        <p>{ t( T_CUSTOMER_SERVICE_SERVICES_CONTENT ) }</p>
                    </section>

                    <section className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info-item' }>
                        <h3>{ t( T_CUSTOMER_SERVICE_IT_CONSULTING_TITLE ) }</h3>
                        <p>{ t( T_CUSTOMER_SERVICE_IT_CONSULTING_CONTENT ) }</p>
                    </section>

                </div>

                <div className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info' }>
                    <section className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info-item' }>
                        <h3>{ t( T_CUSTOMER_SERVICE_CLOUD_CONSULTING_TITLE ) }</h3>
                        <p>{ t( T_CUSTOMER_SERVICE_CLOUD_CONSULTING_CONTENT ) }</p>
                    </section>

                    <section className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info-item' }>
                        <h3>{ t( T_CUSTOMER_SERVICE_GLOBAL_PARTNER_TITLE ) }</h3>
                        <p>{ t( T_CUSTOMER_SERVICE_GLOBAL_PARTNER_CONTENT ) }</p>
                    </section>

                    <section className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-info-item' }>
                        <h3>{ t( T_CUSTOMER_SERVICE_SOFTWARE_CONSULTING_TITLE ) }</h3>
                        { ComponentUtils.prepareParagraphNodes( t, [ T_CUSTOMER_SERVICE_SOFTWARE_CONSULTING_CONTENT ], paramBlocks, translationParams ) }
                    </section>

                </div>

                <div className={ CUSTOMER_SERVICE_VIEW_CLASS_NAME + '-footer' }>
                    <p>{ t( T_CUSTOMER_SERVICE_FOOTER_CONTENT ) }</p>
                </div>

            </article>

        </SEODiv>
);
}
